import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  useScrollTrigger,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from './Logo';

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll(props: HideOnScrollProps) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const LandingHeader: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Features', path: '/features' },
    { text: 'How It Works', path: '/how-it-works' },
    { text: 'Pricing', path: '/pricing' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Logo variant="primary" size="medium" />
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            component={RouterLink} 
            to={item.path} 
            key={item.text}
            sx={{ 
              textAlign: 'center',
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.08)'
              }
            }}
          >
            <ListItemText 
              primary={item.text} 
              primaryTypographyProps={{ 
                fontWeight: 500,
                fontSize: '1rem'
              }}
            />
          </ListItem>
        ))}
        <ListItem 
          button 
          onClick={() => {
            // Close the drawer
            handleDrawerToggle();
            // Get the current URL parameters to check if there's a feature parameter
            const currentUrl = window.location.href;
            const url = new URL(currentUrl);
            const featureParam = url.searchParams.get('feature');
            
            // Navigate to login page and pass the feature parameter if it exists
            if (featureParam) {
              window.location.href = `/login?feature=${featureParam}`;
            } else {
              window.location.href = '/login';
            }
            
            // Ensure page scrolls to top
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 100);
          }}
          sx={{ 
            textAlign: 'center',
            my: 1,
            mx: 2,
            borderRadius: 2,
            py: 1.5,
            '&:hover': {
              bgcolor: 'rgba(33, 150, 243, 0.08)'
            }
          }}
        >
          <ListItemText 
            primary="Login" 
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '1rem'
            }}
          />
        </ListItem>
        
        <ListItem 
          button 
          onClick={() => {
            // Close the drawer
            handleDrawerToggle();
            // Get the current URL parameters to check if there's a feature parameter
            const currentUrl = window.location.href;
            const url = new URL(currentUrl);
            const featureParam = url.searchParams.get('feature');
            
            // Navigate to register page and pass the feature parameter if it exists
            if (featureParam) {
              window.location.href = `/register?feature=${featureParam}`;
            } else {
              window.location.href = '/register';
            }
            
            // Ensure page scrolls to top
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 100);
          }}
          sx={{ 
            textAlign: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            my: 1,
            mx: 2,
            borderRadius: 2,
            py: 1.5,
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          <ListItemText 
            primary="Sign Up Free" 
            primaryTypographyProps={{ 
              fontWeight: 600,
              fontSize: '1rem'
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={scrolled ? 2 : 0}
          sx={{
            bgcolor: scrolled 
              ? 'rgba(255, 255, 255, 0.95)' 
              : 'transparent',
            color: scrolled ? 'text.primary' : 'white',
            borderBottom: scrolled ? '1px solid rgba(58, 54, 224, 0.08)' : 'none',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: scrolled ? 'blur(20px)' : 'none',
            boxShadow: scrolled ? '0 8px 30px rgba(58, 54, 224, 0.08)' : 'none',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ py: { xs: 1, md: 1.5 } }}>
              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
                <RouterLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                  <Logo variant={scrolled ? 'primary' : 'light'} size="medium" />
                </RouterLink>
              </Box>
              
              <Box sx={{ flexGrow: 1 }} />
              
              {/* Desktop menu */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.path}
                    sx={{ 
                      mx: 1, 
                      color: scrolled ? 'text.primary' : 'white',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: scrolled 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
                <Button 
                  variant={scrolled ? "outlined" : "text"} 
                  component={RouterLink}
                  to="/login"
                  sx={{ 
                    mx: 1,
                    color: scrolled ? 'primary.main' : 'white',
                    borderColor: scrolled ? 'primary.main' : 'transparent',
                    '&:hover': {
                      bgcolor: scrolled ? 'rgba(33, 150, 243, 0.04)' : 'rgba(255, 255, 255, 0.1)',
                      borderColor: scrolled ? 'primary.main' : 'white'
                    }
                  }}
                >
                  Login
                </Button>
                <Button 
                  variant="contained" 
                  component={RouterLink}
                  to="/register"
                  sx={{ 
                    ml: 2,
                    px: 3,
                    py: 1,
                    borderRadius: 12,
                    fontWeight: 600,
                    background: scrolled 
                      ? 'linear-gradient(45deg, #3a36e0 30%, #6e6ff7 90%)' 
                      : 'linear-gradient(45deg, #3a36e0 30%, #6e6ff7 90%)',
                    color: 'white',
                    textTransform: 'none',
                    boxShadow: '0 4px 14px 0 rgba(58, 54, 224, 0.3)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(58, 54, 224, 0.4)',
                      background: scrolled 
                        ? 'linear-gradient(45deg, #2a26b9 30%, #5a5be6 90%)' 
                        : 'linear-gradient(45deg, #2a26b9 30%, #5a5be6 90%)'
                    }
                  }}
                >
                  Sign Up Free
                </Button>
              </Box>
              
              {/* Mobile menu icon */}
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Toolbar spacer */}
      <Toolbar sx={{ mb: { xs: 2, md: 3 } }} />
    </>
  );
};

export default LandingHeader; 